import { useState } from "react";
import type { ApiError } from "@/api/client";
import type { Task } from "@/api/tasks";
import {
  cancelQueuedDirectMessage,
  decideDirectApproval,
  queueDirectMessage,
  sendDirectMessage,
  stopDirectTurn,
} from "@/api/task-workers";
import {
  Button,
  CONCEPT_ICONS,
  PocketRow,
  PocketSection,
  TextInput,
  type StatusTone,
} from "@/components/ui";
import { useTaskWorker } from "@/hooks/use-task-worker";
import { useDirectSessionProgress } from "@/stores/direct-session-progress-store";
import {
  directSessionOpenLabel,
  directSessionProviderName,
} from "@/components/shapes/taskboard-frame/task-state";
import { translate, useT } from "@/i18n";
import { commonMessages } from "@/i18n/messages/common";
import { panelsTaskSessionMessages } from "@/i18n/messages/panels-task-session";
import { renderActivityLabel } from "@/lib/activity-label";
import { copyToClipboard } from "@/lib/copy-to-clipboard";
import { openExternalUrl } from "@/lib/open-external-url";

const STATUS_LABEL_KEYS = {
  starting: "statusStarting",
  running: "statusRunning",
  waiting_approval: "statusWaitingApproval",
  idle: "statusIdle",
  failed: "statusFailed",
  closed: "statusClosed",
} as const;

function sessionStatusLabel(status: string): string {
  return status in STATUS_LABEL_KEYS
    ? translate(
        panelsTaskSessionMessages,
        STATUS_LABEL_KEYS[status as keyof typeof STATUS_LABEL_KEYS],
      )
    : status;
}

const SESSION_TONES: Partial<Record<string, StatusTone>> = {
  starting: "working",
  running: "working",
  waiting_approval: "question",
  failed: "error",
};

function actionErrorMessage(error: unknown): string {
  const detail = (error as ApiError | null)?.data?.error;
  if (typeof detail === "string") return detail;
  if (typeof error === "string" && error.trim()) return error;
  return error instanceof Error
    ? error.message
    : translate(panelsTaskSessionMessages, "actionFailed");
}

function isBusyError(error: unknown): boolean {
  return (error as ApiError | null)?.data?.busy === true;
}

/** Direct-session controls on the task panel: status, open-in-app, send,
 *  stop, queued message, and tool approvals. Renders nothing without a
 *  bound direct session. */
export function TaskDirectSessionSection({
  projectId,
  task,
}: {
  projectId: string;
  task: Task;
}) {
  const t = useT(panelsTaskSessionMessages);
  const tc = useT(commonMessages);
  const session = task.direct_session;
  const { worker, refresh } = useTaskWorker(projectId, task.id);
  const progress = useDirectSessionProgress(task.id);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const [busyNotice, setBusyNotice] = useState(false);

  if (!session) return null;
  const testId = "task-pocket-direct-session";
  const ChatIcon = CONCEPT_ICONS.chat;
  const openLabel = directSessionOpenLabel(session.provider);
  const liveStatus =
    worker?.execution_mode === "direct" ? worker.status : session.status;
  const directWorker = worker?.execution_mode === "direct" ? worker : null;
  const turnLive =
    liveStatus === "running" || liveStatus === "waiting_approval";
  // The live event wins over the worker poll, which lags up to 5 s.
  const appWaiting =
    turnLive &&
    (progress ? progress.waiting : Boolean(directWorker?.app_waiting_tool));
  const progressLine =
    turnLive && progress && !progress.waiting
      ? renderActivityLabel(progress.label, progress.text)
      : null;
  const sessionTone: StatusTone | undefined = appWaiting
    ? "question"
    : SESSION_TONES[liveStatus];
  const statusReason = directWorker?.control_unavailable_reason ?? null;

  async function run(action: () => Promise<unknown>, after?: () => void) {
    setSending(true);
    setError("");
    try {
      await action();
      after?.();
      refresh();
    } catch (err) {
      if (isBusyError(err)) {
        setBusyNotice(true);
      } else {
        setError(actionErrorMessage(err));
      }
    } finally {
      setSending(false);
    }
  }

  async function openInApp() {
    if (!session?.open_in_app_url) return;
    setError("");
    try {
      await openExternalUrl(session.open_in_app_url);
    } catch (err) {
      setError(t("openAppFailed", { reason: actionErrorMessage(err) }));
    }
  }

  return (
    <PocketSection title={t("directSession")} testId={testId}>
      <PocketRow
        testId={`${testId}-status`}
        leading={<ChatIcon className="h-4 w-4 text-muted" aria-hidden="true" />}
        title={directSessionProviderName(session.provider)}
        secondLine={
          session.running_in === "app"
            ? t("statusInApp", { status: sessionStatusLabel(liveStatus) })
            : sessionStatusLabel(liveStatus)
        }
        status={sessionTone ? { tone: sessionTone } : undefined}
        below={
          progressLine ? (
            <p
              className="truncate text-appkit-small text-muted"
              title={progressLine}
              data-testid={`${testId}-progress`}
            >
              {progressLine}
            </p>
          ) : statusReason ? (
            <p
              className="text-appkit-small text-muted"
              data-testid={`${testId}-status-reason`}
            >
              {statusReason}
            </p>
          ) : undefined
        }
        trailing={
          session.open_in_app_url ? (
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={() => void openInApp()}
              title={openLabel}
              aria-label={openLabel}
              data-testid={`${testId}-open-app`}
            >
              {t("open")}
            </Button>
          ) : undefined
        }
      />
      {session.attach_command ? (
        <button
          type="button"
          onClick={() => void copyToClipboard(session.attach_command!)}
          title={session.attach_command}
          className="mx-2 block max-w-full truncate rounded-control-sm text-left font-mono text-appkit-small text-muted transition-colors hover:text-primary"
          data-testid={`${testId}-attach-command`}
        >
          {session.attach_command}
        </button>
      ) : null}
      {appWaiting ? (
        <PocketRow
          testId={`${testId}-app-waiting`}
          title={t("mayBeWaiting")}
          secondLine={
            directWorker?.app_waiting_tool
              ? t("toRunTool", { tool: directWorker.app_waiting_tool })
              : t("answerInApp")
          }
          status={{ tone: "question" }}
          trailing={
            session.open_in_app_url ? (
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={() => void openInApp()}
                data-testid={`${testId}-app-waiting-open`}
              >
                {openLabel}
              </Button>
            ) : undefined
          }
        />
      ) : null}
      {session.has_pending_approval && session.pending_approval_id ? (
        <PocketRow
          testId={`${testId}-approval`}
          title={t("asksForApproval")}
          status={{ tone: "question" }}
          below={
            <div className="flex gap-2 pt-1.5">
              <Button
                type="button"
                variant="secondary"
                size="sm"
                disabled={sending}
                onClick={() =>
                  void run(() =>
                    decideDirectApproval(
                      projectId,
                      task.id,
                      session.pending_approval_id!,
                      true,
                    ),
                  )
                }
                data-testid={`${testId}-approval-allow`}
              >
                {t("allow")}
              </Button>
              <Button
                type="button"
                variant="secondary"
                size="sm"
                disabled={sending}
                onClick={() =>
                  void run(() =>
                    decideDirectApproval(
                      projectId,
                      task.id,
                      session.pending_approval_id!,
                      false,
                    ),
                  )
                }
                data-testid={`${testId}-approval-deny`}
              >
                {t("deny")}
              </Button>
            </div>
          }
        />
      ) : null}
      <div className="flex items-center gap-1.5 px-2 pt-1.5">
        <TextInput
          shape="capsule"
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          placeholder={t("messagePlaceholder")}
          disabled={sending}
          aria-label={t("messageLabel")}
          data-testid={`${testId}-message-input`}
          onKeyDown={(event) => {
            if (event.key === "Enter" && draft.trim()) {
              void run(
                () => sendDirectMessage(projectId, task.id, draft.trim()),
                () => setDraft(""),
              );
            }
          }}
        />
        <Button
          type="button"
          variant="secondary"
          size="sm"
          disabled={sending || !draft.trim()}
          onClick={() =>
            void run(
              () => sendDirectMessage(projectId, task.id, draft.trim()),
              () => setDraft(""),
            )
          }
          data-testid={`${testId}-send`}
        >
          {t("send")}
        </Button>
        {worker?.can_pause || liveStatus === "running" ? (
          <Button
            type="button"
            variant="secondary"
            size="sm"
            disabled={sending}
            onClick={() => void run(() => stopDirectTurn(projectId, task.id))}
            data-testid={`${testId}-stop`}
          >
            {t("stop")}
          </Button>
        ) : null}
      </div>
      {busyNotice || error ? (
        <div
          className="flex flex-col gap-1.5 px-2 pt-1.5"
          data-testid={`${testId}-busy`}
        >
          <p className="text-appkit-small font-medium text-muted">
            {error || t("chatBusy")}
          </p>
          {busyNotice && !error ? (
            <div className="flex gap-2">
              <Button
                type="button"
                variant="secondary"
                size="sm"
                disabled={sending || !draft.trim()}
                onClick={() =>
                  void run(
                    () => queueDirectMessage(projectId, task.id, draft.trim()),
                    () => {
                      setDraft("");
                      setBusyNotice(false);
                    },
                  )
                }
                data-testid={`${testId}-queue`}
              >
                {t("sendWhenFinished")}
              </Button>
              {session.open_in_app_url ? (
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={() => void openInApp()}
                  data-testid={`${testId}-open-app-busy`}
                >
                  {openLabel}
                </Button>
              ) : null}
            </div>
          ) : null}
        </div>
      ) : null}
      {session.has_queued_message ? (
        <PocketRow
          testId={`${testId}-queued`}
          title={t("messageQueued")}
          secondLine={t("goesWhenFinished")}
          trailing={
            <Button
              type="button"
              variant="secondary"
              size="sm"
              disabled={sending}
              onClick={() =>
                void run(() => cancelQueuedDirectMessage(projectId, task.id))
              }
              data-testid={`${testId}-queue-cancel`}
            >
              {tc("cancel")}
            </Button>
          }
        />
      ) : null}
    </PocketSection>
  );
}
